'use client'

import { FileText } from 'react-feather'
import { useRouter, useTranslations } from '@/lib/next-shim'

import { GetMyInfosQuery } from '@/core/graphql/types'

import { ExpertiseCard } from '@/entities/expertise'

type ExpertisesProps = {
  expertises: GetMyInfosQuery['myInfos']['expertises']
}

function ExpertisesEmptyState() {
  const t = useTranslations()
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
        <FileText size={22} aria-hidden="true" />
      </span>
      <p className="text-sm font-semibold text-gray-900">{t('account.expertises.noExpertises')}</p>
      <p className="max-w-md text-sm text-gray-500">{t('account.expertises.noExpertisesDescription')}</p>
    </div>
  )
}

function CountBadge({ value }: { value: number }) {
  return <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-primary">{value}</span>
}

export function Expertises({ expertises }: ExpertisesProps) {
  const t = useTranslations()
  const router = useRouter()

  const hasExpertises = expertises.length > 0
  const goToDetail = (id: string) => router.push(`/account/expertises/${id}`)

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
      <div className="flex items-center gap-2.5">
        <h1 className="section-title text-xl md:text-2xl">{t('account.expertises.title')}</h1>
        {hasExpertises && <CountBadge value={expertises.length} />}
      </div>

      {hasExpertises ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {expertises.map((expertise) => (
            <ExpertiseCard key={expertise._id} expertise={expertise} onClick={() => goToDetail(expertise._id)} />
          ))}
        </div>
      ) : (
        <ExpertisesEmptyState />
      )}
    </div>
  )
}
